import { CustomerTile } from "@/components/molecules/customer-tile";
import { DossierFiles } from "@/components/molecules/dossier-files";
import { LinkedSessions } from "@/components/molecules/linked-sessions";
import { Markdown } from "@/components/molecules/markdown";
import { UnsureNameMark } from "@/components/molecules/unsure-name-mark";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { useHasPermission } from "@/features/clients/hooks/use-permissions";
import {
  deadlineWasPromised,
  messageIsLate,
  messageSessionIds,
  type ClientMessage,
} from "@/features/messages/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import {
  ArrowLeftIcon,
  ClockIcon,
  PhoneCallIcon,
  PhoneIcon,
  RobotIcon,
  UserIcon,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { HandleMessageMenu } from "./handle-menu";
import { MessageHistory } from "./message-history";
import { MessageTimeline } from "./message-timeline";
import { MessageMenu } from "./message-menu";
import { MessageTasks } from "./message-tasks";
import { RelatedMessages } from "./related-messages";
import { messageStatusKey } from "./message-list-item";
import { CategoryBadge, DeadlineEditor, UrgencyBadge } from "./triage";

/**
 * The right pane: one request, in full, and the answer to it right underneath —
 * what the dialog over the table used to hold, without leaving the list.
 */
export function MessageView({
  message,
  onBack,
  onResolved,
}: {
  message: ClientMessage;
  onBack?: () => void;
  /** Once the request is closed, so the page can move on to the next one. */
  onResolved?: (message: ClientMessage) => void;
}) {
  const { t } = useTranslation("messages");
  const { format } = useDateFormat();
  const canManage = useHasPermission("messages:manage");
  const { data: agents = [] } = useAgents();

  const pending = message.status === "pending";
  const late = messageIsLate(message);
  const agentName = agents.find((a) => a.id === message.agent_id)?.name;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-start gap-2 border-b p-4">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            className="-ml-2 shrink-0 md:hidden"
            onClick={onBack}
            aria-label={t("view.back")}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <h2 className="text-lg font-semibold leading-tight">
            {message.subject}
          </h2>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={pending ? "outline" : "secondary"}>
              {message.resolution === "delegated" && (
                <PhoneCallIcon className="mr-1 h-3 w-3" weight="fill" />
              )}
              {t(`status.${messageStatusKey(message)}`)}
            </Badge>
            <CategoryBadge message={message} editable={pending && canManage} />
            <UrgencyBadge message={message} editable={pending && canManage} />
            {late && (
              <Badge
                variant="outline"
                className="border-amber-500/40 text-amber-600"
              >
                <ClockIcon className="mr-1 h-3 w-3" weight="fill" />
                {t("table.late")}
              </Badge>
            )}
          </div>
        </div>
        {pending && canManage && (
          <HandleMessageMenu
            // A fresh menu per request.
            key={message.id}
            message={message}
            onDone={(kind) => kind === "resolved" && onResolved?.(message)}
          />
        )}
        <MessageMenu message={message} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
          <div className="space-y-3">
            {message.customer_id ? (
              <CustomerTile
                customerId={message.customer_id}
                fallback={message.customer_name || t("table.unknownCustomer")}
                variant="inline"
                clickable
              />
            ) : (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="flex items-center gap-2 font-medium">
                  <span className="flex size-7 items-center justify-center rounded-full bg-primary/10">
                    <UserIcon className="size-4 text-primary" weight="duotone" />
                  </span>
                  {message.customer_name || t("table.unknownCustomer")}
                  {message.customer_name && message.unsure_customer_name && (
                    <UnsureNameMark className="-ml-1.5" />
                  )}
                </span>
                {message.phone_number && (
                  <span className="flex items-center gap-1 tabular-nums text-muted-foreground">
                    <PhoneIcon className="size-3.5 shrink-0" />
                    {message.phone_number}
                  </span>
                )}
              </div>
            )}

            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <Fact label={t("table.received")}>
                {message.created_at ? format(message.created_at, "Pp") : "-"}
              </Fact>
              {(!!message.due_at || (pending && canManage)) && (
                <Fact
                  label={t(
                    deadlineWasPromised(message)
                      ? "view.promisedFor"
                      : "view.deadline",
                  )}
                >
                  <DeadlineEditor message={message} editable={pending && canManage}>
                    <span
                      className={
                        late ? "font-medium text-amber-600 dark:text-amber-400" : ""
                      }
                    >
                      {message.due_at ? format(message.due_at, "Pp") : "-"}
                    </span>
                  </DeadlineEditor>
                </Fact>
              )}
              <Fact label={t("table.assignedTo")}>
                {message.assigned_member_name || (
                  <span className="text-muted-foreground">
                    {t("table.anyone")}
                  </span>
                )}
              </Fact>
              {agentName && (
                <Fact label={t("view.agent")}>
                  <span className="flex items-center gap-1">
                    <RobotIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    {agentName}
                  </span>
                </Fact>
              )}
            </dl>
          </div>

          <section className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              {t("view.request")}
            </h3>
            <Markdown className="rounded-md bg-muted/50 p-3">
              {message.body}
            </Markdown>
          </section>

          <MessageTasks message={message} canManage={canManage} />

          {message.customer_id && (
            <DossierFiles
              customerId={message.customer_id}
              source={{ origin: "message", originId: message.id }}
              title={t("view.files")}
            />
          )}

          <RelatedMessages message={message} />

          <LinkedSessions sessionIds={messageSessionIds(message)} />

          {/* Comments stay possible on a closed request: the customer may still
              be owed one, and the AI may reopen it within 48 hours. */}
          <MessageTimeline message={message} canManage={canManage} />

          {!pending && !!message.handled_at && (
            <p className="text-sm">
              {t("view.handledAt", { date: format(message.handled_at, "Pp") })}
              {message.resolution === "delegated" &&
                ` · ${t("view.callbackPlanned")}`}
            </p>
          )}

          {pending && !canManage && (
            <p className="text-xs text-muted-foreground">{t("view.readOnly")}</p>
          )}

          <MessageHistory messageId={message.id} />
        </div>
      </div>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}
